import { FunctionComponent, useRef } from 'react'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'

import HelpSection from 'components/display/HelpSection'
import LazyImage from 'components/display/LazyImage'
import ButtonLink from 'components/display/ButtonLink'
import Brief from 'components/landing/Brief'
// import FAQ from '../shared/FAQ'
import Header from 'components/landing/Header'
import InfoParagraph from 'components/landing/InfoParagraph'
import MasonryImages from 'components/landing/MasonryImages'
import SectionIntro from 'components/landing/SectionIntro'
import SinglePromoImage from 'components/landing/SinglePromoImage'
import StoryCredit from 'components/landing/StoryCredit'
import Testimonials from 'components/landing/Testimonials'
import {
  Container,
  ImageColumn,
  PageCTALink,
  ResponsiveTwoColumnLayout,
  SpacedColumn,
} from 'components/landing/display'

import {
  benefitsImages,
  benefitsPromoImage,
  benefitsText,
  briefText,
  // faqItems,
  headerImage,
  headerText,
  howItWorksImage,
  howItWorksText,
  standalonePromoImage,
  testimonials,
} from './content'

const StoriesLandingStoryteller: FunctionComponent<{}> = () => {
  const { xl: largerThanXl, md: isLargerThanMd } = useMediaQueryContext()

  const briefEl = useRef<HTMLDivElement>()
  const headerImgEl = useRef<HTMLImageElement>()

  return (
    <>
      <Header
        cta={
          <ButtonLink
            href="https://help.riparide.com/en/articles/6707031-storyteller-applications"
            target="_blank"
            rel="noopener noreferrer"
          >
            Become a Storyteller
          </ButtonLink>
        }
        h1Text="Riparide Stories"
        h2Text={headerText.heading}
        h3Text={headerText.subheading}
      >
        <LazyImage
          ref={headerImgEl}
          data-src={headerImage.large.url}
          data-srcset={`
            ${headerImage.medium.url} ${headerImage.medium.width}w,
            ${headerImage.large.url} ${headerImage.large.width}w,
            ${headerImage.hero.url} ${headerImage.hero.width}w,
          `}
          preload
        />
      </Header>

      <Container ref={briefEl}>
        <Brief text={briefText} />
      </Container>

      <Container
        style={largerThanXl ? { paddingLeft: 0, paddingRight: 0 } : {}}
      >
        <SinglePromoImage
          src={standalonePromoImage.large.url}
          data-srcset={`
            ${standalonePromoImage.hero.url} ${standalonePromoImage.hero.width}w,
            ${standalonePromoImage.large.url} ${standalonePromoImage.large.width}w,
          `}
          annotationComp={
            <StoryCredit
              avatar={{
                thumbLg:
                  'https://assets.riparide.com/user/21250/avatar/thumb_lg-ca7ddb2a475fec21660c11bbd7f92847.png',
              }}
              firstName="Charlotte"
              storyTitle="Disconnect to Reconnect"
              storyUrl="/stories/222-disconnect-to-reconnect"
            />
          }
        />
      </Container>
      <Container>
        <SectionIntro
          primaryHeading="Why become a Storyteller?"
          secondaryHeading="Riparide Stories"
          paragraphText="Enjoy adventure time, creative freedom and inspiring others to live fully."
        />
        <ResponsiveTwoColumnLayout>
          <ImageColumn>
            <SinglePromoImage
              src={benefitsPromoImage.large.url}
              data-src={`
                ${benefitsPromoImage.large.url} ${benefitsPromoImage.large.width}w,
                ${benefitsPromoImage.medium.url} ${benefitsPromoImage.medium.width}w
              `}
              annotationComp={
                <StoryCredit
                  avatar={{
                    thumbLg:
                      'https://assets.riparide.com/user/7856/avatar/thumb_lg-8f75cd65126d20a7aa7e8ef9ac87ebd8.jpg',
                  }}
                  firstName="Alexandra"
                  storyTitle="Recuperate & Connect"
                  storyUrl="/stories/587-recuperate-connect"
                />
              }
            />
          </ImageColumn>
          <SpacedColumn>
            {benefitsText.map(({ heading, text }) => (
              <InfoParagraph heading={heading} text={text} />
            ))}
          </SpacedColumn>
        </ResponsiveTwoColumnLayout>
      </Container>

      <Container style={{ marginBottom: 0 }}>
        <MasonryImages
          images={benefitsImages}
          imagesPerColumn={{
            1: 3,
            2: 4,
            3: 4,
          }}
        />

        <SectionIntro
          primaryHeading="Meet our storytellers"
          paragraphText="Here’s what our Storytellers think about Riparide Stories. "
          style={{ marginBottom: isLargerThanMd ? 60 : 40 }}
        />
      </Container>

      <Testimonials testimonials={testimonials} />

      <Container style={{ marginTop: 120 }}>
        <SectionIntro
          primaryHeading="How it works"
          secondaryHeading="Riparide Stories"
          paragraphText="Join the community, lock in your adventure and share your story."
        />
        <ResponsiveTwoColumnLayout>
          <ImageColumn>
            <SinglePromoImage
              src={howItWorksImage.large.url}
              data-src={`
                ${howItWorksImage.large.url} ${howItWorksImage.large.width}w,
                ${howItWorksImage.medium.url} ${howItWorksImage.medium.width}w
              `}
              annotationComp={
                <StoryCredit
                  avatar={{
                    thumbLg:
                      'https://assets.riparide.com/user/267/avatar/thumb_lg-e6ca6266826f4a5d7ad594479e28b76f.jpg',
                  }}
                  firstName="Andrew & Jessie"
                  storyTitle="Home Away from Home"
                  storyUrl="/stories/320-home-away-from-home"
                />
              }
            />
          </ImageColumn>
          <SpacedColumn>
            {howItWorksText.map(({ heading, text }) => (
              <InfoParagraph heading={heading} text={text} />
            ))}
          </SpacedColumn>
        </ResponsiveTwoColumnLayout>
        <PageCTALink
          href="https://help.riparide.com/en/articles/6707031-storyteller-applications"
          target="_blank"
          rel="noopener noreferrer"
        >
          Become a Storyteller
        </PageCTALink>
        {/* <FAQ items={faqItems} /> */}
      </Container>

      <Container>
        <HelpSection
          alignment="center"
          link="https://help.riparide.com/en/collections/1866296-storytelling"
        />
      </Container>
    </>
  )
}

export default StoriesLandingStoryteller
